import React, { useState } from 'react';
import { Plus, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Button } from '../components/ui/button';
import { PartnerList } from '../components/PartnerList';
import { PartnerForm } from '../components/PartnerForm';
import { PartnerDetail } from '../components/PartnerDetail';
import { PartnerDto } from '../lib/api/types';

// Layout component
const Layout = ({ children }: { children: React.ReactNode }) => (
  <div className="min-h-screen bg-background">
    <header className="border-b">
      <div className="container mx-auto px-4 py-4">
        <h1 className="text-2xl font-bold">ProPay Simulator Admin</h1>
      </div>
    </header>
    <main className="container mx-auto px-4 py-8">
      {children}
    </main>
  </div>
);

type ViewMode = 'list' | 'create' | 'edit' | 'detail';

const PartnersPage: React.FC = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedPartner, setSelectedPartner] = useState<PartnerDto | null>(null);

  const handleCreateNew = () => {
    setSelectedPartner(null);
    setViewMode('create');
  };

  const handleEdit = (partner: PartnerDto) => {
    setSelectedPartner(partner);
    setViewMode('edit');
  };

  const handleView = (partner: PartnerDto) => {
    setSelectedPartner(partner);
    setViewMode('detail');
  };

  const handleBackToList = () => {
    setSelectedPartner(null);
    setViewMode('list');
  };

  const handleFormSuccess = () => {
    setViewMode('list');
    setSelectedPartner(null);
  };

  const renderContent = () => {
    switch (viewMode) {
      case 'create':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Create New Partner</h2>
              <Button onClick={handleBackToList} variant="outline">
                Back to Partners
              </Button>
            </div>
            <PartnerForm
              onSuccess={handleFormSuccess}
              onCancel={handleBackToList}
            />
          </div>
        );

      case 'edit':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Edit Partner</h2>
              <Button onClick={handleBackToList} variant="outline">
                Back to Partners
              </Button>
            </div>
            <PartnerForm
              partner={selectedPartner || undefined}
              onSuccess={handleFormSuccess}
              onCancel={handleBackToList}
            />
          </div>
        );

      case 'detail':
        return (
          <PartnerDetail
            partnerId={selectedPartner?.partnerId}
            onEdit={handleEdit}
            onBack={handleBackToList}
          />
        );

      default:
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Button onClick={() => navigate('/')} variant="outline">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Home
                </Button>
                <h2 className="text-xl font-semibold">Partners</h2>
              </div>
              <Button onClick={handleCreateNew}>
                <Plus className="mr-2 h-4 w-4" />
                Create Partner
              </Button>
            </div>
            <PartnerList
              onCreatePartner={handleCreateNew}
              onEditPartner={handleEdit}
              onViewPartner={handleView}
            />
          </div>
        );
    }
  };

  return (
    <Layout>
      {renderContent()}
    </Layout>
  );
};

export default PartnersPage;
